import React, { useEffect, useMemo, useState } from 'react';
import { Text, Tooltip } from 'components';
import { IDaySegment, IEmployerSite, PermissionType } from 'types';
import { hasSitePermission } from 'utils/employers';
import { AddButton, EnabledWrapper } from '../../../styles';
import { DaySegmentForm } from './DaySegmentForm';
import { DaySegmentItem } from './DaySegmentItem';

interface IDaySegmentsListProps {
  currentSite: IEmployerSite;
  daySegments?: IDaySegment[];
}

export const DaySegmentsList: React.FC<IDaySegmentsListProps> = ({ currentSite, daySegments }) => {
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editDaySegmentId, setEditDaySegmentId] = useState<string | null>(null);

  const addPermission = hasSitePermission(currentSite?.permissions, PermissionType.SITE_DAYSEGMENT_CREATE);
  const editPermission = hasSitePermission(currentSite?.permissions, PermissionType.SITE_DAYSEGMENT_UPDATE);
  const deletePermission = hasSitePermission(currentSite?.permissions, PermissionType.SITE_DAYSEGMENT_DELETE);

  useEffect(() => {
    setAddFormOpen(false);
    setEditDaySegmentId(null);
  }, [currentSite.id]);

  const mappedDaySegments = useMemo(
    () =>
      daySegments?.map((daySegment) =>
        editDaySegmentId === daySegment.id ? (
          <DaySegmentForm key={daySegment.id} daySegment={daySegment} siteId={currentSite.id} onClose={() => setEditDaySegmentId(null)} />
        ) : (
          <DaySegmentItem key={daySegment.id} daySegment={daySegment} editPermission={editPermission} deletePermission={deletePermission} onEdit={() => setEditDaySegmentId(daySegment.id)} disabled={addFormOpen || !!editDaySegmentId} />
        )
      ),
    [daySegments, currentSite.id, editDaySegmentId, addFormOpen, editPermission, deletePermission]
  );

  return (
    <>
      <EnabledWrapper>
        <Text size="xsmall">You must configure at least one day segment. If you pool tips, use multiple day segments to create separate pools for different shifts (e.g., AM and PM).</Text>
      </EnabledWrapper>

      {mappedDaySegments}

      {addFormOpen && <DaySegmentForm siteId={currentSite.id} onClose={() => setAddFormOpen(false)} />}

      <AddButton appearance="white" icon="Plus" label="Add Day Segment" className="day-segment-add-button" onClick={() => setAddFormOpen(true)} disabled={addFormOpen || !!editDaySegmentId || !addPermission} tooltipId="no-add-permission" />
      {!addPermission && (
        <Tooltip id="no-add-permission">
          You do not have permission to add day segments.
          <br />
          Contact your supervisor for more information.
        </Tooltip>
      )}
    </>
  );
};
